// v10/src/components/admin/inventory/ByCategoryView.js
//
// Vista por categoría del Stock de repuestos (A8) — Tarea 1 (lectura).
// Carga GET /api/inventory-items con los filtros aplicados, agrupa el
// resultado por categoría y refresca con polling cuando isActive=true.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import inventoryService from '../../../services/inventoryService';
import { POLLING_INTERVALS } from '../../../config/realtime';
import InventoryFiltersBar from './InventoryFiltersBar';
import InventoryItemsList from './InventoryItemsList';

const INITIAL_FILTERS = {
  search: '',
  category: '',
  tracking_mode: '',
  low_stock: false,
};

// Convierte el filtro local en query params para el backend.
// `is_active=true` lo pone el backend por defecto.
const buildParams = (filters) => {
  const out = {};
  if (filters?.search)        out.search        = filters.search;
  if (filters?.category)      out.category      = filters.category;
  if (filters?.tracking_mode) out.tracking_mode = filters.tracking_mode;
  if (filters?.low_stock)     out.low_stock     = 'true';
  return out;
};

const ByCategoryView = ({
  darkMode,
  locale = 'es-AR',
  isActive = true,
  initialFilters = INITIAL_FILTERS,
  onItemsChange,
  onItemAction,
  canEdit = true,
  canDelete = true,
  refreshKey = 0,
}) => {
  const { t } = useTranslation('admin');
  const [filters, setFilters] = useState(initialFilters || INITIAL_FILTERS);
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const pollRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    setError('');
    try {
      const res = await inventoryService.list(buildParams(filters));
      const data = res?.data?.data || [];
      if (!mountedRef.current) return;
      setItems(data);
      if (onItemsChange) onItemsChange(data);
    } catch (_err) {
      if (!mountedRef.current) return;
      setError(t('inventory_error_loading'));
    } finally {
      if (mountedRef.current && showLoader) setLoading(false);
    }
  }, [filters, onItemsChange, t]);

  // Carga inicial / al cambiar filtros — con loader visible.
  useEffect(() => {
    load(true);
  }, [load]);

  // Refresh externo (post-acción de modales)
  useEffect(() => {
    if (refreshKey > 0) load(false);
    // eslint-disable-next-line
  }, [refreshKey]);

  // Polling — pausado si la pestaña no está activa o la pestaña del browser está oculta.
  useEffect(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (!isActive) return undefined;
    pollRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      load(false);
    }, POLLING_INTERVALS.inventory);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [isActive, load]);

  const isFiltered = !!(filters.search || filters.category || filters.tracking_mode || filters.low_stock);

  const handleClearFilters = () => setFilters(INITIAL_FILTERS);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <InventoryFiltersBar
        filters={filters}
        onChange={setFilters}
        darkMode={darkMode}
      />

      <InventoryItemsList
        items={items}
        loading={loading}
        error={error || null}
        groupBy="category"
        emptyVariant={isFiltered ? 'no_results' : 'no_items'}
        onClearFilters={isFiltered ? handleClearFilters : null}
        onRetry={() => load(true)}
        darkMode={darkMode}
        locale={locale}
        onItemAction={onItemAction}
        canEdit={canEdit}
        canDelete={canDelete}
      />
    </div>
  );
};

export default ByCategoryView;
